import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { packages as packagesApi, coaches as coachesApi, bookings as bookingsApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { getSeasonDates } from "../utils/season";

const LEVEL_OPTIONS = [
  { value: "beginner",     label: "初學者", desc: "從未滑過雪或經驗很少" },
  { value: "intermediate", label: "中級",   desc: "能自行上下山，想精進技術" },
  { value: "advanced",     label: "進階",   desc: "熟練各種地形，挑戰高難度" },
];

function formatTime(t) {
  if (!t) return "";
  return t.slice(0, 5);
}

function toDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("zh-TW", { year: "numeric", month: "long", day: "numeric", weekday: "long" });
}

const STEPS = ["選擇日期", "選擇方案", "程度與人數", "選擇教練", "確認預約"];

export default function BookingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep]                         = useState(0);
  const [pkgList, setPkgList]                   = useState([]);
  const [availableCoaches, setAvailableCoaches] = useState([]);

  const [selectedDate, setSelectedDate]         = useState("");
  const [selectedPkg, setSelectedPkg]           = useState(null);
  const [selectedLevel, setSelectedLevel]       = useState("");
  const [participantCount, setParticipantCount] = useState(1);
  const [specifyCoach, setSpecifyCoach]         = useState(false);
  const [selectedCoach, setSelectedCoach]       = useState(null);
  const [notes, setNotes]                       = useState("");
  const [meetingPoint, setMeetingPoint]         = useState("");

  const [loading, setLoading]       = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [result, setResult]         = useState(null);

  const seasonDates = getSeasonDates();

  useEffect(() => {
    packagesApi.list().then(setPkgList).catch(err => setError(err.message));
  }, []);

  useEffect(() => {
    if (step === 3 && selectedDate && selectedPkg) {
      setLoading(true);
      coachesApi.available(selectedDate, selectedPkg.id)
        .then(setAvailableCoaches)
        .catch(err => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [step, selectedDate, selectedPkg]);

  const participantFee = (participantCount - 1) * 1000;
  const referralFee    = specifyCoach && selectedCoach ? 1000 : 0;
  const totalPrice     = selectedPkg ? selectedPkg.price + participantFee + referralFee : 0;

  const handleNext = () => {
    setError("");
    if (step === 0 && !selectedDate)  { setError("請選擇日期"); return; }
    if (step === 1 && !selectedPkg)   { setError("請選擇課程方案"); return; }
    if (step === 2 && !selectedLevel) { setError("請選擇程度"); return; }
    if (step === 3 && specifyCoach && !selectedCoach) { setError("請選擇教練或改為不指定"); return; }
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    if (!user) { navigate("/login"); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await bookingsApi.create({
        package_id:        selectedPkg.id,
        booking_date:      selectedDate,
        student_level:     selectedLevel,
        participant_count: participantCount,
        coach_id:          specifyCoach && selectedCoach ? selectedCoach.id : null,
        notes:             notes || undefined,
        meeting_point:     meetingPoint || undefined,
      });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", padding: "0 24px", textAlign: "center" }}>
        <div className="card" style={{ padding: 40 }}>
          <div style={{
            width: 72, height: 72, borderRadius: 18,
            background: "var(--ink)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 20px", overflow: "hidden",
          }}>
            <img src="/logo.png" alt="馭雪 YUXUE" style={{ width: "72%", height: "72%", objectFit: "contain" }} />
          </div>
          <h2 style={{ fontSize: 24, marginBottom: 8 }}>預約成功！</h2>
          <p style={{ color: "var(--muted)", marginBottom: 8 }}>
            請前往我的預約完成付款
          </p>
          <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 24 }}>
            預約編號 #{result.booking_id}
          </p>
          <button className="btn btn-primary btn-full" onClick={() => navigate("/bookings")}>
            查看我的預約
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "40px 24px" }}>

      <button onClick={() => step > 0 ? setStep(s => s - 1) : navigate("/")}
        style={{ background: "none", color: "var(--muted)", fontSize: 14, cursor: "pointer", marginBottom: 24, display: "flex", alignItems: "center", gap: 6 }}>
        ← {step > 0 ? "上一步" : "返回首頁"}
      </button>

      <h1 style={{ fontSize: 28, marginBottom: 8 }}>預約課程</h1>

      <div style={{ display: "flex", gap: 0, marginBottom: 36, marginTop: 20 }}>
        {STEPS.map((label, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", marginBottom: 6,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 600,
              background: i <= step ? "var(--ink)" : "var(--stone)",
              color: i <= step ? "var(--white)" : "var(--muted)",
            }}>
              {i < step ? "✓" : i + 1}
            </div>
            <div style={{ fontSize: 11, color: i === step ? "var(--ink)" : "var(--muted)", fontWeight: i === step ? 600 : 400, textAlign: "center" }}>
              {label}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ background: "#FEF2F2", color: "var(--red)", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 28 }}>

        {step === 0 && (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 8 }}>選擇上課日期</h2>
            <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 20 }}>
              開放預約日期：雪季期間（12月 – 4月）
            </p>
            {seasonDates.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: "var(--muted)" }}>
                目前非雪季預約期間，雪季將於 12 月開放預約
              </div>
            ) : (
              <select
                className="input-field"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
              >
                <option value="">請選擇日期</option>
                {seasonDates.map(d => (
                  <option key={d} value={d}>{formatDisplayDate(d)}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {step === 1 && (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>選擇課程方案</h2>
            <div style={{ display: "grid", gap: 12 }}>
              {pkgList.map(pkg => (
                <div
                  key={pkg.id}
                  onClick={() => setSelectedPkg(pkg)}
                  style={{
                    border: `2px solid ${selectedPkg?.id === pkg.id ? "var(--ink)" : "var(--stone)"}`,
                    borderRadius: 12, padding: "16px 20px", cursor: "pointer",
                    background: selectedPkg?.id === pkg.id ? "var(--off)" : "var(--white)",
                    transition: "all .15s",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{pkg.name}</div>
                      <div style={{ color: "var(--muted)", fontSize: 14 }}>
                        {formatTime(pkg.start_time)} – {formatTime(pkg.end_time)}
                        <span style={{ marginLeft: 12 }}>計費 {pkg.duration_hours} 小時</span>
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 18 }}>
                      NT$ {Number(pkg.price).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>您的滑雪程度</h2>
            <div style={{ display: "grid", gap: 12, marginBottom: 28 }}>
              {LEVEL_OPTIONS.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => setSelectedLevel(opt.value)}
                  style={{
                    border: `2px solid ${selectedLevel === opt.value ? "var(--ink)" : "var(--stone)"}`,
                    borderRadius: 12, padding: "16px 20px", cursor: "pointer",
                    background: selectedLevel === opt.value ? "var(--off)" : "var(--white)",
                    transition: "all .15s",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{opt.label}</div>
                  <div style={{ color: "var(--muted)", fontSize: 14 }}>{opt.desc}</div>
                </div>
              ))}
            </div>

            <div style={{ borderTop: "1px solid var(--stone)", paddingTop: 24 }}>
              <h3 style={{ fontSize: 16, marginBottom: 6 }}>上課人數</h3>
              <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
                每增加 1 人加收 NT$ 1,000，最多 4 人
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                {[1, 2, 3, 4].map(n => (
                  <button
                    key={n}
                    onClick={() => setParticipantCount(n)}
                    className="btn"
                    style={{
                      width: 56, height: 56, fontSize: 18, fontWeight: 600,
                      background: participantCount === n ? "var(--ink)" : "var(--white)",
                      color: participantCount === n ? "var(--white)" : "var(--ink)",
                      border: `2px solid ${participantCount === n ? "var(--ink)" : "var(--stone)"}`,
                      borderRadius: 12,
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {participantCount > 1 && (
                <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 10 }}>
                  人數加收 NT$ {((participantCount - 1) * 1000).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>選擇教練</h2>
            <div style={{ display: "grid", gap: 12, marginBottom: 20 }}>
              <div
                onClick={() => { setSpecifyCoach(false); setSelectedCoach(null); }}
                style={{
                  border: `2px solid ${!specifyCoach ? "var(--ink)" : "var(--stone)"}`,
                  borderRadius: 12, padding: "16px 20px", cursor: "pointer",
                  background: !specifyCoach ? "var(--off)" : "var(--white)",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>不指定教練</div>
                <div style={{ color: "var(--muted)", fontSize: 14 }}>系統自動依程度分配最合適的教練</div>
              </div>

              <div
                onClick={() => setSpecifyCoach(true)}
                style={{
                  border: `2px solid ${specifyCoach ? "var(--ink)" : "var(--stone)"}`,
                  borderRadius: 12, padding: "16px 20px", cursor: "pointer",
                  background: specifyCoach ? "var(--off)" : "var(--white)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>指定教練</div>
                    <div style={{ color: "var(--muted)", fontSize: 14 }}>加收 NT$ 1,000 指定費</div>
                  </div>
                  <span style={{ fontSize: 13, color: "var(--ink2)", background: "var(--stone)", borderRadius: 6, padding: "2px 8px" }}>+NT$1,000</span>
                </div>
              </div>
            </div>

            {specifyCoach && (
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 8, color: "var(--ink2)" }}>
                  選擇教練
                </label>
                {loading ? (
                  <div style={{ textAlign: "center", padding: 20 }}>
                    <span className="spinner" style={{ margin: "0 auto" }} />
                  </div>
                ) : availableCoaches.length === 0 ? (
                  <div style={{ color: "var(--muted)", fontSize: 14, padding: 12 }}>
                    此日期與方案目前無可指定的教練
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {availableCoaches.map(c => (
                      <div
                        key={c.id}
                        onClick={() => setSelectedCoach(c)}
                        style={{
                          border: `1.5px solid ${selectedCoach?.id === c.id ? "var(--ink)" : "var(--stone)"}`,
                          borderRadius: 10, padding: "12px 16px", cursor: "pointer",
                          background: selectedCoach?.id === c.id ? "var(--off)" : "var(--white)",
                          display: "flex", alignItems: "center", gap: 14,
                        }}
                      >
                        <div style={{
                          width: 40, height: 40, borderRadius: "50%",
                          background: "var(--ink)", color: "var(--white)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 16, flexShrink: 0, overflow: "hidden",
                        }}>
                          {c.avatar_url
                            ? <img src={c.avatar_url} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : c.name?.[0]
                          }
                        </div>
                        <div>
                          <div style={{ fontWeight: 500 }}>{c.name}</div>
                          <div style={{ fontSize: 13, color: "var(--muted)" }}>{c.years_experience} 年經驗</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 style={{ fontSize: 20, marginBottom: 20 }}>確認預約資訊</h2>
            <div style={{ background: "var(--off)", borderRadius: 10, padding: "16px 20px", marginBottom: 20 }}>
              {[
                ["上課日期", formatDisplayDate(selectedDate)],
                ["課程方案", `${selectedPkg?.name}（${formatTime(selectedPkg?.start_time)} – ${formatTime(selectedPkg?.end_time)}）`],
                ["程度",     LEVEL_OPTIONS.find(l => l.value === selectedLevel)?.label],
                ["人數",     `${participantCount} 人`],
                ["教練",     specifyCoach && selectedCoach ? `${selectedCoach.name}（指定）` : "系統自動分配"],
              ].map(([label, value]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--stone)" }}>
                  <span style={{ color: "var(--muted)", fontSize: 14 }}>{label}</span>
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{value}</span>
                </div>
              ))}

              <div style={{ paddingTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <span style={{ color: "var(--muted)", fontSize: 13 }}>方案費用</span>
                  <span style={{ fontSize: 13 }}>NT$ {Number(selectedPkg?.price).toLocaleString()}</span>
                </div>
                {participantCount > 1 && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                    <span style={{ color: "var(--muted)", fontSize: 13 }}>人數加收（{participantCount - 1} 人 × NT$1,000）</span>
                    <span style={{ fontSize: 13 }}>NT$ {participantFee.toLocaleString()}</span>
                  </div>
                )}
                {specifyCoach && selectedCoach && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                    <span style={{ color: "var(--muted)", fontSize: 13 }}>指定教練費</span>
                    <span style={{ fontSize: 13 }}>NT$ 1,000</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10, borderTop: "1px solid var(--stone)", marginTop: 8 }}>
                  <span style={{ fontWeight: 700 }}>費用合計</span>
                  <span style={{ fontWeight: 700, fontSize: 20 }}>NT$ {totalPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>
                集合地點（選填）
              </label>
              <input className="input-field" placeholder="例：纜車站入口"
                value={meetingPoint} onChange={e => setMeetingPoint(e.target.value)} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--ink2)" }}>
                備註（選填）
              </label>
              <textarea className="input-field" rows={3} placeholder="其他需求或注意事項"
                value={notes} onChange={e => setNotes(e.target.value)}
                style={{ resize: "vertical" }} />
            </div>
          </div>
        )}

      </div>

      <div style={{ marginTop: 20 }}>
        {step < 4 ? (
          <button className="btn btn-primary btn-full" style={{ height: 48 }} onClick={handleNext}>
            下一步
          </button>
        ) : (
          <button
            className="btn btn-primary btn-full"
            style={{ height: 48 }}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting
              ? <span className="spinner" style={{ borderTopColor: "var(--white)" }} />
              : user ? "確認送出預約" : "登入後預約"}
          </button>
        )}
      </div>
    </div>
  );
}